import React from 'react';
import { connectedFilterFields } from './connectedFilterFields';
import { Box, Button, FadeInView, HStack, ScrollView } from '~/native-ui';
import { WithFilterChip } from './FilterChip';
import { Filter } from '../Fields/VariadicField/VariadicField';
import { connect } from '~/redux/connect';
import { settingsActionCreator } from '~/actions/settingsActions';
import { Animated } from 'react-native';
import takeWhile from 'lodash/takeWhile';

const FilterChip = connect(null, (dispatch, { modelId, name }) => ({
    updateFilterOrder: () => dispatch(settingsActionCreator(modelId).updateFilterOrder(name)),
}))(WithFilterChip(Filter));

const NavigationButton = React.memo(({ icon, label, onPress, ...props }) => {
    const iconProps = React.useRef({
        icon,
        size: 'xs',
        padding: '10px',
        background: 'white',
        borderRadius: '100px',
        iconProps: { size: 'xs', color: 'dark', iconFamily: 'material-community' },
    }).current;

    return (
        <FadeInView {...props}>
            <Button {...iconProps} label={label} onPress={onPress} />
        </FadeInView>
    );
});

export const FilterList = connectedFilterFields({ withAllFilters: true, withSearchFilter: false })(
    React.memo((props) => {
        const [chipOffsets, setChipOffset] = React.useState([]),
            [showNavigation, setShowNavigation] = React.useState(true),
            scrollRef = React.useRef(null),
            scrollPositionRef = React.useRef(null),
            offsetRefTimeout = React.useRef(null),
            destroyTimer = React.useCallback(() => {
                clearTimeout(offsetRefTimeout.current);
                offsetRefTimeout.current = null;
            }),
            onLayout = (i, { nativeEvent }) => {
                destroyTimer();
                chipOffsets[i] = nativeEvent.layout.x;
                offsetRefTimeout.current = setTimeout(() => setChipOffset((chipoffset) => [...chipoffset]), 100);
            },
            animation = React.useRef(new Animated.Value(0)).current,
            animate = (toValue) => {
                return Animated.spring(animation, {
                    toValue: toValue,
                    useNativeDriver: false,
                    isInteraction: true,
                    bounciness: 2,
                    speed: 10,
                }).start();
            },
            onScroll = React.useCallback(({ nativeEvent }) => {
                const {
                        contentOffset: { x: xOffset },
                        layoutMeasurement: { width: scrollWidth },
                        contentSize: { width: contentWidth },
                    } = nativeEvent,
                    value = xOffset <= 0 ? 0 : contentWidth > Math.round(xOffset + scrollWidth) ? 1 : 2;

                scrollPositionRef.current = nativeEvent;
                animate(value);
            }, []),
            iconWrapperProps = {
                position: 'absolute',
                top: '0px',
            },
            inputRange = [0, 1, 2],
            leftIconProps = React.useRef({
                style: {
                    ...iconWrapperProps,
                    left: 0,
                    zIndex: animation.interpolate({
                        inputRange,
                        outputRange: ['-100', '0', '0'],
                    }),
                },
                inputRange,
                opacityOutputRange: ['0', '1', '1'],
                transformOutputRange: ['0px', '0px', '0px'],
                animation,
            }).current,
            rightIconProps = React.useRef({
                style: {
                    ...iconWrapperProps,
                    right: 0,
                    zIndex: animation.interpolate({
                        inputRange,
                        outputRange: ['0', '0', '-100'],
                    }),
                },
                inputRange,
                opacityOutputRange: ['1', '1', '0'],
                transformOutputRange: ['0px', '0px', '0px'],
                animation,
            }).current,
            label = props.appliedFilterCount
                ? props.trans('filters.applied_filter_count', { count: props.appliedFilterCount })
                : null,
            scrollTo = (getPosition, toEnd) => {
                const xOffset = scrollPositionRef.current?.contentOffset.x || 0,
                    floored = chipOffsets.map(Math.floor).sort((a, b) => a - b),
                    [newOffset, nextOffset = newOffset, farNext = nextOffset] = getPosition(
                        floored,
                        Math.floor(xOffset)
                    );

                if (!farNext) {
                    toEnd
                        ? scrollRef.current.scrollToEnd({ animated: true })
                        : scrollRef.current.scrollTo({ x: 0, animated: true });

                    return;
                }

                const scrollDiff = !toEnd
                    ? farNext
                    : props.maxScroll
                    ? {
                          true: Math.min(xOffset + props.maxScroll, farNext),
                          false: Math.max(xOffset + props.maxScroll, farNext),
                      }[!!toEnd]
                    : farNext;

                scrollRef.current.scrollTo({ x: scrollDiff, animated: true });
            },
            moveLeft = React.useCallback(() => {
                scrollTo((positions, offset) => takeWhile(positions, (x) => x < offset).reverse(), false);
            }, [chipOffsets]),
            moveRight = React.useCallback(() => {
                scrollTo((positions, offset) => takeWhile(positions.reverse(), (x) => x > offset).reverse(), true);
            }, [chipOffsets]),
            handleLayout = React.useCallback(
                (scrollWidth) => {
                    if (!scrollRef.current || showNavigation === scrollWidth > scrollRef.current.clientWidth) return;
                    setShowNavigation(!showNavigation);
                },
                [showNavigation]
            ),
            renderNavigation = () => {
                if (!showNavigation) return null;

                return (
                    <>
                        <NavigationButton {...leftIconProps} onPress={moveLeft} label={label} icon="arrow-left" />
                        <NavigationButton {...rightIconProps} onPress={moveRight} icon="arrow-right" />
                    </>
                );
            };

        React.useEffect(() => destroyTimer, [chipOffsets]);

        return (
            <Box position="relative" flex="1">
                <ScrollView
                    onContentSizeChange={handleLayout}
                    ref={scrollRef}
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    snapToOffsets={chipOffsets}
                    scrollEventThrottle={10}
                    onScroll={onScroll}
                    borderRadius="100px"
                >
                    <HStack space="2">
                        {props.filterNames.map((filter, i) => {
                            return (
                                <Box onLayout={(e) => onLayout(i, e)} key={i}>
                                    <FilterChip
                                        key={filter.name}
                                        name={filter.name}
                                        modelId={props.modelId}
                                        updateFilterOrder={props.updateFilterOrder}
                                    />
                                </Box>
                            );
                        })}
                    </HStack>
                </ScrollView>
                {renderNavigation()}
            </Box>
        );
    })
);


FilterList.displayName = 'FilterList';